// Build a self-contained, print-styled HTML document for an agent's
// commission report and open it in a new tab with `window.print()` queued.
// We intentionally render to a separate document (not into the current
// page DOM) so the shadcn Dialog portal, app chrome, header, and toast
// containers don't bleed into the printed output.

const fmtUSD = (n) => new Intl.NumberFormat("en-US", {
  style: "currency", currency: "USD", maximumFractionDigits: 2,
}).format(Number(n || 0));

const safe = (s) => (s == null ? "—" : String(s));

const escapeHtml = (s) => safe(s)
  .replace(/&/g, "&amp;")
  .replace(/</g, "&lt;")
  .replace(/>/g, "&gt;")
  .replace(/"/g, "&quot;")
  .replace(/'/g, "&#39;");

/**
 * @param {object} agent  /admin/agents/{id} response payload
 * @param {array}  rows   /admin/agents/{id}/commissions response rows
 */
export function buildAgentPrintableReport(agent, rows) {
  const c = agent?.commissions || {};
  const generatedAt = new Date();
  const generatedLine = generatedAt.toLocaleString("en-US", {
    dateStyle: "long", timeStyle: "short",
  });

  const last4Acct = agent?.bank_account_number
    ? `•••• ${String(agent.bank_account_number).slice(-4)}`
    : "—";
  const last4Routing = agent?.routing_number
    ? `•••• ${String(agent.routing_number).slice(-4)}`
    : "—";

  const commissionRowsHtml = (rows || []).map((r) => `
    <tr>
      <td>${escapeHtml(new Date(r.earned_at).toLocaleString())}</td>
      <td>${escapeHtml(r.customer_email)}</td>
      <td>${escapeHtml(r.transaction_type)}</td>
      <td class="num">${escapeHtml(fmtUSD(r.transaction_amount))}</td>
      <td class="num strong">${escapeHtml(fmtUSD(r.commission_amount))}</td>
      <td>${r.status === "paid" ? '<span class="badge paid">Paid</span>' : '<span class="badge unpaid">Unpaid</span>'}</td>
      <td class="mono small">${escapeHtml(r.paid_ref || "—")}</td>
    </tr>
  `).join("");

  const html = `<!doctype html>
<html lang="en">
<head>
<meta charset="utf-8" />
<title>Zcardon · Commission Report · ${escapeHtml(agent?.full_name)} (${escapeHtml(agent?.agent_code)})</title>
<style>
  * { box-sizing: border-box; }
  body {
    font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", "Helvetica Neue", Arial, sans-serif;
    color: #112318;
    background: #fff;
    margin: 0;
    padding: 32px 40px;
    -webkit-print-color-adjust: exact;
    print-color-adjust: exact;
  }
  .doc { max-width: 880px; margin: 0 auto; }
  .brand { font-size: 11px; letter-spacing: 0.3em; text-transform: uppercase; color: #4A5C50; }
  h1 { font-family: "Cormorant Garamond", "Times New Roman", Georgia, serif; font-weight: 500; font-size: 38px; margin: 4px 0 0; letter-spacing: -0.5px; }
  h2 { font-family: "Cormorant Garamond", "Times New Roman", Georgia, serif; font-weight: 500; font-size: 22px; margin: 24px 0 8px; border-bottom: 1px solid #E5E0D8; padding-bottom: 4px; }
  .sub { color: #4A5C50; font-size: 13px; margin-top: 6px; }
  .meta-grid { display: grid; grid-template-columns: repeat(2, 1fr); gap: 6px 32px; margin-top: 14px; font-size: 12px; }
  .meta-grid dt { color: #4A5C50; }
  .meta-grid dd { margin: 0; font-weight: 600; }
  .stats {
    display: grid; grid-template-columns: repeat(4, 1fr); gap: 12px; margin-top: 16px;
  }
  .stat { border: 1px solid #E5E0D8; border-radius: 10px; padding: 12px; text-align: center; }
  .stat .label { font-size: 10px; letter-spacing: 0.2em; text-transform: uppercase; color: #4A5C50; }
  .stat .val { font-family: "Cormorant Garamond", Georgia, serif; font-size: 22px; margin-top: 6px; }
  .stat.unpaid .val { color: #b45309; }
  .stat.lifetime .val { color: #184A30; }
  table { width: 100%; border-collapse: collapse; margin-top: 8px; font-size: 12px; }
  th, td { padding: 8px 10px; border-bottom: 1px solid #E5E0D8; text-align: left; vertical-align: top; }
  th { font-size: 10px; letter-spacing: 0.15em; text-transform: uppercase; color: #4A5C50; }
  td.num, th.num { text-align: right; font-variant-numeric: tabular-nums; font-family: ui-monospace, "SF Mono", Menlo, monospace; }
  td.strong { font-weight: 600; color: #184A30; }
  td.mono, .mono { font-family: ui-monospace, "SF Mono", Menlo, monospace; }
  td.small, .small { font-size: 11px; }
  .badge { display: inline-block; padding: 2px 8px; border-radius: 999px; font-size: 9px; letter-spacing: 0.15em; text-transform: uppercase; font-weight: 700; }
  .badge.paid { background: #dcfce7; color: #166534; }
  .badge.unpaid { background: #fef3c7; color: #92400e; }
  .footer { margin-top: 28px; font-size: 11px; color: #4A5C50; border-top: 1px solid #E5E0D8; padding-top: 10px; display: flex; justify-content: space-between; }
  .actions { position: fixed; top: 12px; right: 16px; }
  .actions button {
    background: #184A30; color: #fff; border: 0; padding: 8px 14px; border-radius: 8px;
    font-size: 12px; cursor: pointer; letter-spacing: 0.1em; text-transform: uppercase;
  }
  .actions button.ghost { background: transparent; color: #184A30; border: 1px solid #E5E0D8; margin-right: 8px; }
  @media print {
    .actions { display: none; }
    body { padding: 0 24px; }
    h2 { page-break-after: avoid; }
    tr { page-break-inside: avoid; }
  }
</style>
</head>
<body>
  <div class="actions">
    <button class="ghost" onclick="window.close()">Close</button>
    <button onclick="window.print()">Print</button>
  </div>
  <div class="doc">
    <div class="brand">Zcardon · Agent Commission Report</div>
    <h1>${escapeHtml(agent?.full_name)} <span style="font-family: ui-monospace, monospace; font-size: 18px; color: #4A5C50;">· ${escapeHtml(agent?.agent_code)}</span></h1>
    <p class="sub">${escapeHtml(agent?.email)} · ${escapeHtml(agent?.phone)}</p>

    <dl class="meta-grid">
      <div><dt>Address</dt><dd>${escapeHtml(agent?.address)}</dd></div>
      <div><dt>Commission rate</dt><dd>${escapeHtml(agent?.commission_percent)}%</dd></div>
      <div><dt>Bank</dt><dd>${escapeHtml(agent?.bank_name)}</dd></div>
      <div><dt>Account holder</dt><dd>${escapeHtml(agent?.bank_account_holder)}</dd></div>
      <div><dt>Account #</dt><dd class="mono">${escapeHtml(last4Acct)}</dd></div>
      <div><dt>Routing #</dt><dd class="mono">${escapeHtml(last4Routing)}</dd></div>
      <div><dt>Status</dt><dd>${agent?.status === "suspended" ? "Suspended" : "Active"}</dd></div>
      <div><dt>Report generated</dt><dd>${escapeHtml(generatedLine)}</dd></div>
    </dl>

    <h2>Commission rollup</h2>
    <div class="stats">
      <div class="stat"><div class="label">Week</div><div class="val">${escapeHtml(fmtUSD(c.week?.total))}</div></div>
      <div class="stat"><div class="label">Month</div><div class="val">${escapeHtml(fmtUSD(c.month?.total))}</div></div>
      <div class="stat"><div class="label">Year</div><div class="val">${escapeHtml(fmtUSD(c.year?.total))}</div></div>
      <div class="stat unpaid"><div class="label">Unpaid</div><div class="val">${escapeHtml(fmtUSD(c.unpaid?.total))}</div></div>
    </div>
    <div class="stats" style="grid-template-columns: repeat(2, 1fr); margin-top: 8px;">
      <div class="stat lifetime"><div class="label">Lifetime earned</div><div class="val">${escapeHtml(fmtUSD(c.lifetime?.total))}</div></div>
      <div class="stat"><div class="label">Lifetime cards</div><div class="val">${escapeHtml(c.lifetime?.count ?? 0)}</div></div>
    </div>

    <h2>Commission rows (${(rows || []).length})</h2>
    ${(rows || []).length === 0 ? `
      <p class="sub" style="text-align:center; padding: 20px 0;">No commission rows recorded for this agent.</p>
    ` : `
      <table>
        <thead>
          <tr>
            <th>Earned</th>
            <th>Customer</th>
            <th>Type</th>
            <th class="num">Txn $</th>
            <th class="num">Commission</th>
            <th>Status</th>
            <th>Paid ref</th>
          </tr>
        </thead>
        <tbody>${commissionRowsHtml}</tbody>
      </table>
    `}

    <div class="footer">
      <span>Zcardon · Confidential · Generated ${escapeHtml(generatedLine)}</span>
      <span class="mono">Agent ${escapeHtml(agent?.agent_code)}</span>
    </div>
  </div>
  <script>
    // Auto-trigger the native print dialog once the document has rendered.
    // Wrapped in setTimeout so Chrome/Safari finish layout before the
    // print preview snapshots the page.
    window.addEventListener("load", function () {
      setTimeout(function () { window.print(); }, 250);
    });
  </script>
</body>
</html>`;

  const w = window.open("", "_blank", "noopener,noreferrer,width=900,height=1100");
  if (!w) {
    // Popup blocked — fall back to a data URL navigation in same tab.
    const blob = new Blob([html], { type: "text/html" });
    const url = URL.createObjectURL(blob);
    window.open(url, "_blank");
    return { popupBlocked: true };
  }
  w.document.open();
  w.document.write(html);
  w.document.close();
  return { popupBlocked: false };
}
